import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { Cache } from './cache.service';
import {
  IProduct2,
  IProductType,
  ListProductClassificationsRequest,
  ListProductClassificationsResponse,
  ListProductTypesRequest,
  ListProductTypesResponse,
  ListProductSpecificationsRequest,
  ListProductSpecificationsResponse,
  ProductSpecificationFilterOption,
  ListProductsRequest,
  ListProductsResponse,
  NewListProductsRequest,
  NewListProductsResponse,
  NewGetProductRequest,
  NewGetProductResponse,
  Product2,
  ProductClassification,
  ProductType,
  ProductSpecification,
  RequestedProduct,
  ProductWorkflow,
  ProductWorkflowTrigger,
  ProductWorkflowHistory,
  ThemeReplacementTag,
  SituViewWebApiClient,
  UpdateProductMatchRequest,
  UpdateProductMatchResponse,
  AddProductClassificationRequest,
  AddProductClassificationResponse,
  UpdateProductClassificationRequest,
  UpdateProductClassificationResponse,
  NewAddProductRequest,
  NewAddProductResponse,
  NewUpdateProductRequest,
  NewUpdateProductResponse,
  AddProductTypeRequest,
  AddProductTypeResponse,
  UpdateProductTypeRequest,
  UpdateProductTypeResponse,
  UpdateProductTypeRelatedTagsRequest,
  UpdateProductTypeRelatedTagsResponse,
  ListRequestedProductsRequest,
  ListRequestedProductsResponse,
  UpdateRequestedProduct,
  UpdateRequestedProductRequest,
  UpdateRequestedProductResponse,
  UpdateRequestedProductsRequest,
  UpdateRequestedProductsResponse,
  AddProductSpecificationRequest,
  AddProductSpecificationResponse,
  UpdateProductSpecificationRequest,
  UpdateProductSpecificationResponse,
  ListThemeReplacementTagsRequest,
  ListThemeReplacementTagsResponse,
  TriggerProductWorkflowRequest,
  TriggerProductWorkflowResponse,
  TriggerRequestedProductWorkflowRequest,
  TriggerRequestedProductWorkflowResponse,
  GetProductWorkflowHistoryRequest,
  GetProductWorkflowHistoryResponse,
  GetProductWorkflowConfigurationRequest,
  GetProductWorkflowConfigurationResponse,
  ProductWorkflowState,
  GetProductThemesRequest,
  GetProductThemesResponse,
  Theme,
  UpdateThemeReplacementTagRequest,
  ListRequestedProductsRequestOrderField,
  ListProductsRequestOrderField,
  GetListProductsFiltersRequest,
  GetListProductsFiltersResponse,
  GetListRequestedProductsFiltersRequest,
  GetListRequestedProductsFiltersResponse
} from 'situview-webapi-client';
import { ICache } from './theme.service';
import { CreateGenericArtifactRequest, CreateGenericArtifactResponse, EnvironmentName, ProductName, SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  cacheKey: Guid = Guid.create();
  oldCacheKey: Guid = Guid.create();
  private readonly fiveMinuteCacheDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
  private readonly thirtyMinuteCacheDuration = 30 * 60 * 1000; // 30 minutes in milliseconds

  constructor(
    private situViewClient: SituViewWebApiClient,
    private renderServiceClient: SituRenderServiceWebApiClient
  )  {
    this.oldCacheKey = this.cacheKey;
  }

  ListProducts(request: ListProductsRequest, refreshData: boolean = false): Promise<ListProductsResponse> {
    if (refreshData) {
      this.RefreshData();
      return this.listProductsInternal(request, this.cacheKey, this.oldCacheKey);
    } else {
      return this.listProductsInternal(request, this.cacheKey, this.oldCacheKey);
    }
  }

  UpdateProductMatch(request: UpdateProductMatchRequest): Promise<UpdateProductMatchResponse> {
    return this.situViewClient.updateProductMatch(request)
      .then((response: UpdateProductMatchResponse) => {
        if (response.success) {
          this.RefreshData();
        }
        return response;
      });
  }

  AddProduct(product: Product2): Promise<Product2 | undefined> {
    var request = new NewAddProductRequest();
    request.product = product;
    return this.situViewClient.newAddProduct(request)
    .then((response: NewAddProductResponse) => {
      if (response.success) {
        return response.product;
      } else {
        return undefined;
      }
    });
  }

  UpdateProduct(product: Product2): Promise<Product2 | undefined> {
    var request = new NewUpdateProductRequest();
    request.product = product;
    return this.situViewClient.newUpdateProduct(request)
    .then((response: NewUpdateProductResponse) => {
      if (response.success) {
        return response.product;
      } else {
        return undefined;
      }
    });
  }

  GetProduct(productId: string, includeProductSpecifications: boolean = false, includeCurrentState: boolean = false, includeProductRanges: boolean = false, includeBrand: boolean = false): Promise<IProduct2 | undefined> {
    var request = new NewGetProductRequest();
    request.productId = productId;
    request.includeProductSpecifications = includeProductSpecifications;
    request.includeCurrentState = includeCurrentState;
    request.includeProductRanges = includeProductRanges;
    request.includeBrand = includeBrand;

    return this.situViewClient.newGetProduct(request)
    .then((response: NewGetProductResponse) => {
      if (response.success) {
        return response.product;
      } else {
        return undefined;
      }
    });
  }

  LoadProducts(companyId: string, filterCompanyId?: string, productIds?: string[], relatedTagsOrder?: string[], includeProductSpecifications: boolean = false, includeCurrentState: boolean = false): Promise<Product2[]> {
    var request = new NewListProductsRequest();
    request.companyId = companyId;
    request.filterCompanyId = filterCompanyId;
    request.includeProductSpecifications = includeProductSpecifications;
    request.includeCurrentState = includeCurrentState;

    if (productIds) request.filterProductIds = productIds;

    if (relatedTagsOrder) request.orderRelatedTags = relatedTagsOrder;
    
    return this.situViewClient.newListProducts(request)
      .then((response: NewListProductsResponse) => {
        if (response.success) {
          return response.items!;
        } else {
          return [];
        }
      });
  }

  async LoadPaginatedProducts(companyId: string,
    filterProductIds?: string[], filterCompanyId?: string, filterBrandId?: string, filterProductClassificationId?: string, filterProductTypeId?: string, filterProductTypeTemplateName?: string, filterProductState?: ProductWorkflowState, searchTerm?: string, filterRangeId?: string,
    relatedTagsOrder?: string[], 
    includeProductSpecifications: boolean = false, includeCurrentState: boolean = false, includeRanges: boolean = false, includeBrand: boolean = false,
    filterEnabled: boolean | undefined = undefined, filterTagGroups: string[] | undefined = undefined,
    orderBy = ListProductsRequestOrderField.Unset, orderDescending = false,
    pageNumber?: number, pageSize?: number): Promise<PaginatedProducts> {
    var listProductsRequest = new NewListProductsRequest();
    listProductsRequest.companyId = companyId;

    listProductsRequest.includeProductSpecifications = includeProductSpecifications;
    listProductsRequest.includeCurrentState = includeCurrentState;
    listProductsRequest.includeRanges = includeRanges;
    listProductsRequest.includeBrand = includeBrand
    listProductsRequest.filterEnabled = filterEnabled;

    if (pageNumber) listProductsRequest.pageNumber = pageNumber;
    if (pageSize) listProductsRequest.pageSize = pageSize;

    if (filterProductIds) listProductsRequest.filterProductIds = filterProductIds;
    if (filterCompanyId) listProductsRequest.filterCompanyId = filterCompanyId;
    if (filterBrandId) listProductsRequest.filterBrandId = filterBrandId;
    if (filterProductClassificationId) listProductsRequest.filterProductClassificationId = filterProductClassificationId;
    if (filterProductTypeId) listProductsRequest.filterProductTypeId = filterProductTypeId;
    if (filterProductTypeTemplateName) listProductsRequest.filterProductTypeTemplateName = filterProductTypeTemplateName;
    if (filterProductState) listProductsRequest.filterProductState = filterProductState;
    if (filterRangeId) listProductsRequest.filterRangeId = filterRangeId;
    if (filterTagGroups) listProductsRequest.filterTagGroups = filterTagGroups;

    if (searchTerm) listProductsRequest.searchTerm = searchTerm;

    if (relatedTagsOrder) listProductsRequest.orderRelatedTags = relatedTagsOrder;

    if (orderBy != ListProductsRequestOrderField.Unset) {
      listProductsRequest.orderBy = orderBy;
      listProductsRequest.orderDescending = orderDescending;
    }

    var listProductsResponse = await this.situViewClient.newListProducts(listProductsRequest);

    var paginatedProducts;
    if (listProductsResponse.success) {
      paginatedProducts = new PaginatedProducts(listProductsResponse.items, listProductsResponse.pageNumber, listProductsResponse.pageSize, listProductsResponse.totalItems);
    } else {
      paginatedProducts = new PaginatedProducts();
    }

    return paginatedProducts;
  }

  async LoadPaginatedRequestedProducts(companyId?: string,
    filterImportCompanyId?: string, filterImportId?: string, filterSupplierId?: string, filterRequestedProductState?: ProductWorkflowState, filterProductClassificationId?: string, filterProductTypeId?: string, filterProductTypeTemplateName?: string, filterBrandId?: string, searchTerm?: string,
    includeCurrentState: boolean = false, includeBrand: boolean = false,
    orderBy = ListRequestedProductsRequestOrderField.Unset, orderDescending = false,
    pageNumber?: number, pageSize?: number): Promise<PaginatedRequestedProducts> {
    var listRequestedProductsRequest = new ListRequestedProductsRequest();

    listRequestedProductsRequest.companyId = companyId;

    listRequestedProductsRequest.includeCurrentState = includeCurrentState;
    listRequestedProductsRequest.includeBrand = includeBrand

    if (pageNumber) listRequestedProductsRequest.pageNumber = pageNumber;
    if (pageSize) listRequestedProductsRequest.pageSize = pageSize;

    if (filterImportCompanyId) listRequestedProductsRequest.filterImportCompanyId = filterImportCompanyId;
    if (filterImportId) listRequestedProductsRequest.filterImportId = filterImportId;
    if (filterSupplierId) listRequestedProductsRequest.filterSupplierId = filterSupplierId;
    if (filterRequestedProductState) listRequestedProductsRequest.filterRequestedProductState = filterRequestedProductState;
    if (filterProductClassificationId) listRequestedProductsRequest.filterProductClassificationId = filterProductClassificationId;
    if (filterProductTypeId) listRequestedProductsRequest.filterProductTypeId = filterProductTypeId;
    if (filterProductTypeTemplateName) listRequestedProductsRequest.filterProductTypeTemplateName = filterProductTypeTemplateName;
    if (filterBrandId) listRequestedProductsRequest.filterBrandId = filterBrandId;

    if (searchTerm) listRequestedProductsRequest.searchTerm = searchTerm;

    if (orderBy != ListRequestedProductsRequestOrderField.Unset) {
      listRequestedProductsRequest.orderBy = orderBy;
      listRequestedProductsRequest.orderDescending = orderDescending;
    }

    var listRequestedProductsResponse = await this.situViewClient.listRequestedProducts(listRequestedProductsRequest);
    
    var paginatedRequestedProducts;
    if (listRequestedProductsResponse.success) {
      paginatedRequestedProducts = new PaginatedRequestedProducts(listRequestedProductsResponse.items, listRequestedProductsResponse.pageNumber, listRequestedProductsResponse.pageSize, listRequestedProductsResponse.totalItems);
    } else {
      paginatedRequestedProducts = new PaginatedRequestedProducts();
    }
    
    return paginatedRequestedProducts;
  }

  UpdateRequestedProduct(requestedProduct: RequestedProduct): Promise<RequestedProduct | undefined> {
    var request = new UpdateRequestedProductRequest();
    request.requestedProduct = this.mapToUpdateRequestedProduct(requestedProduct);

    return this.situViewClient.updateRequestedProduct(request)
      .then((response: UpdateRequestedProductResponse) => {
        if (response.success) {
          return response.requestedProduct;
        } else {
          return undefined;
        };
      });
  }

  UpdateRequestedProducts(requestedProducts: RequestedProduct[]): Promise<RequestedProduct[] | undefined> {
    var request = new UpdateRequestedProductsRequest();
    request.requestedProducts = requestedProducts.map(rp => this.mapToUpdateRequestedProduct(rp));

    return this.situViewClient.updateRequestedProducts(request)
      .then((response: UpdateRequestedProductsResponse) => {
        if (response.success) {
          return response.requestedProducts;
        } else {
          return undefined;
        };
      });
  }

  private mapToUpdateRequestedProduct(rp: RequestedProduct): UpdateRequestedProduct {
    var update = new UpdateRequestedProduct();
    update.id = rp.id;
    update.productTypeId = rp.productTypeId;
    update.supplierId = rp.supplierId;
    update.brandId = rp.brandId;
    update.qualityLevel = rp.qualityLevel;
    update.name = rp.name;
    update.productCode = rp.productCode;
    update.productUrl = rp.productUrl;
    update.description = rp.description;
    update.notes = rp.notes;
    update.duplicateProductId = rp.duplicateProductId;
    update.duplicateRequestedProductId = rp.duplicateRequestedProductId;

    // Build per-row updates for ranges and file vault references
    var rangeUpdates = (rp.ranges || []).map(r => ({
      id: r.id || undefined,
      rangeId: r.range?.id || undefined
    }));

    var fileVaultReferenceUpdates = (rp.fileVaultReferences || []).map(f => ({
      id: f.id || undefined,
      fileVaultReferenceId: f.fileVaultReference?.id || undefined,
      category: f.category
    }));

    // Patch toJSON to include the new fields until the TS client is regenerated
    var originalToJSON = update.toJSON.bind(update);
    update.toJSON = (data?: any) => {
      data = originalToJSON(data);
      data["rangeUpdates"] = rangeUpdates;
      data["fileVaultReferenceUpdates"] = fileVaultReferenceUpdates;
      return data;
    };

    return update;
  }

  async GenerateProductPreview(productCompanyId: string, assetKey: string): Promise<CreateGenericArtifactResponse> {
    let request = new CreateGenericArtifactRequest();
    request.companyId = productCompanyId;
    request.callerReferenceKey = assetKey?.replace("assetinstance\\", "assetpreview\\");
    request.environment = EnvironmentName.Latest;
    request.environmentToUse = ProductName.Panorama;

    return await this.renderServiceClient.createGenericArtifact(request);
  }

  TriggerRequestedProductWorkflow(requestedProductId: string, trigger?: ProductWorkflowTrigger, comments?: string): Promise<ProductWorkflow | undefined> {
    let triggerRequestedProductWorkflowRequest = new TriggerRequestedProductWorkflowRequest();
    triggerRequestedProductWorkflowRequest.requestedProductId = requestedProductId;
    triggerRequestedProductWorkflowRequest.workflowTrigger = trigger;
    triggerRequestedProductWorkflowRequest.comments = comments;

    return this.situViewClient.triggerRequestedProductWorkflow(triggerRequestedProductWorkflowRequest)
      .then((x: TriggerRequestedProductWorkflowResponse) => {
        if (x.success) {
          this.productWorkflowHistoryCache = undefined;
          return x.currentWorkflowState;
        } else {
          return undefined;
        }
      })
      .catch((err: any) => {
        return undefined;
      });
  }

  AddProductClassification(productClassification: ProductClassification): Promise<ProductClassification | undefined> {
    var request = new AddProductClassificationRequest();
    request.productClassification = productClassification;
    return this.situViewClient.addProductClassification(request)
    .then((response: AddProductClassificationResponse) => {
      this.productClassificationsCache = undefined;

      if (response.success) {
        return response.productClassification;
      } else {
        return undefined;
      }
    });
  }

  UpdateProductClassification(productClassification: ProductClassification): Promise<ProductClassification | undefined> {
    var request = new UpdateProductClassificationRequest();
    request.productClassification = productClassification;
    return this.situViewClient.updateProductClassification(request)
    .then((response: UpdateProductClassificationResponse) => {
      this.productClassificationsCache = undefined;
      
      if (response.success) {
        return response.productClassification;
      } else {
        return undefined;
      }
    });
  }
  
  private productClassificationsCache: ICache<ProductClassification> | undefined;
  private loadingProductClassificationsPromise: Promise<ProductClassification[]> | null = null;
  LoadProductClassifications(): Promise<ProductClassification[]> {
    if (this.productClassificationsCache && (Date.now() - this.productClassificationsCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.productClassificationsCache.data);
    } else if (this.loadingProductClassificationsPromise) {
      return this.loadingProductClassificationsPromise;
    } else {
      this.loadingProductClassificationsPromise = this.situViewClient.listProductClassifications(new ListProductClassificationsRequest())
          .then((response: ListProductClassificationsResponse) => {
            if (response.success) {
              const sortedProductClassifications = (response.productClassifications ?? [])
                .sort((a, b) => {
                  const nameA = a.name ?? '';
                  const nameB = b.name ?? '';
                  return nameA.localeCompare(nameB);
                });
              this.productClassificationsCache = {
                timestamp: Date.now(),
                data: sortedProductClassifications ?? []
              };
              return this.productClassificationsCache.data;
            } else {
              return [];
            }
          }).finally(() => {
            this.loadingProductClassificationsPromise = null;
          });
      return this.loadingProductClassificationsPromise;
    }
  }

  AddProductType(productType: ProductType): Promise<ProductType | undefined> {
    var request = new AddProductTypeRequest();
    request.productType = productType;
    return this.situViewClient.addProductType(request)
    .then((response: AddProductTypeResponse) => {
      this.productTypesCache = undefined;

      if (response.success) {
        return response.productType;
      } else {
        return undefined;
      }
    });
  }

  UpdateProductType(productType: ProductType): Promise<ProductType | undefined> {
    var request = new UpdateProductTypeRequest();
    request.productType = productType;
    return this.situViewClient.updateProductType(request)
    .then((response: UpdateProductTypeResponse) => {
      this.productTypesCache = undefined;
      
      if (response.success) {
        return response.productType;
      } else {
        return undefined;
      }
    });
  }

  UpdateProductTypeRelatedTags(productTypeId: string, productTypeRelatedTags: string[]): Promise<boolean> {
    var request = new UpdateProductTypeRelatedTagsRequest();
    request.productTypeId = productTypeId;
    request.productTypeRelatedTags = productTypeRelatedTags;
    return this.situViewClient.updateProductTypeRelatedTags(request)
    .then((response: UpdateProductTypeRelatedTagsResponse) => {
      return response.success!;
    });
  }
  
  private productTypesCache: ICache<ProductType> | undefined;
  private loadingProductTypesPromise: Promise<ProductType[]> | null = null;
  LoadProductTypes(): Promise<ProductType[]> {
    if (this.productTypesCache && (Date.now() - this.productTypesCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.productTypesCache.data);
    } else if (this.loadingProductTypesPromise) {
      return this.loadingProductTypesPromise;
    } else {
      this.loadingProductTypesPromise = this.situViewClient.listProductTypes(new ListProductTypesRequest())
          .then((response: ListProductTypesResponse) => {
            if (response.success) {
              const sortedProductTypes = (response.productTypes ?? [])
                .sort((a, b) => {
                  const nameA = a.name ?? '';
                  const nameB = b.name ?? '';
                  return nameA.localeCompare(nameB);
                });
              this.productTypesCache = {
                timestamp: Date.now(),
                data: sortedProductTypes ?? []
              };
              return this.productTypesCache.data;
            } else {
              return [];
            }
          }).finally(() => {
            this.loadingProductTypesPromise = null;
          });
      return this.loadingProductTypesPromise;
    }
  }

  async getProductTypeColor(productTypeId: string | undefined): Promise<string | undefined> {
    if (!productTypeId) return undefined;
    const productTypes = await this.LoadProductTypesWithColor();
    const productType = productTypes.find(c => c.id === productTypeId);
    return productType?.color;
  }

  async getProductTypeName(productTypeId: string | undefined): Promise<string | undefined> {
    if (!productTypeId) return undefined;
    const productTypes = await this.LoadProductTypesWithColor();
    const productType = productTypes.find(c => c.id === productTypeId);
    return productType?.name;
  }

  LoadProductTypesWithColor(): Promise<ProductTypeWithColor[]> {
    return this.LoadProductTypes()
      .then((sortedProductTypes: ProductType[]) => {
        let colorIndex = 0;
        const productTypesWithColors = sortedProductTypes
          .map(productType => {
            const color = this.colorPool[colorIndex % this.colorPool.length];
            colorIndex++;
            return new ProductTypeWithColor(productType, color);
          });
        return productTypesWithColors ?? [];
      }).finally(() => {
      });
  }

  AddProductSpecification(productSpecification: ProductSpecification): Promise<ProductSpecification | undefined> {
    var request = new AddProductSpecificationRequest();
    request.productSpecification = productSpecification;
    return this.situViewClient.addProductSpecification(request)
    .then((response: AddProductSpecificationResponse) => {
      this.productSpecificationsCache = undefined;

      if (response.success) {
        return response.productSpecification;
      } else {
        return undefined;
      }
    });
  }

  UpdateProductSpecification(productSpecification: ProductSpecification): Promise<ProductSpecification | undefined> {
    var request = new UpdateProductSpecificationRequest();
    request.productSpecification = productSpecification;
    return this.situViewClient.updateProductSpecification(request)
    .then((response: UpdateProductSpecificationResponse) => {
      this.productSpecificationsCache = undefined;
      
      if (response.success) {
        return response.productSpecification;
      } else {
        return undefined;
      }
    });
  }
  
  private productSpecificationsCache: ICache<ProductSpecification> | undefined;
  private loadingProductSpecificationsPromise: Promise<ProductSpecification[]> | null = null;
  LoadProductSpecifications(): Promise<ProductSpecification[]> {
    if (this.productSpecificationsCache && (Date.now() - this.productSpecificationsCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.productSpecificationsCache.data);
    } else if (this.loadingProductSpecificationsPromise) {
      return this.loadingProductSpecificationsPromise;
    } else {
      this.loadingProductSpecificationsPromise = this.situViewClient.listProductSpecifications(new ListProductSpecificationsRequest())
          .then((response: ListProductSpecificationsResponse) => {
            if (response.success) {
              this.productSpecificationsCache = {
                timestamp: Date.now(),
                data: response.productSpecifications ?? []
              };
              return this.productSpecificationsCache.data;
            } else {
              return [];
            }
          }).finally(() => {
            this.loadingProductSpecificationsPromise = null;
          });
      return this.loadingProductSpecificationsPromise;
    }
  }
  
  private productSpecificationFilterOptionsCache: Map<string, { timestamp: number; data: ProductSpecificationFilterOption[] }> = new Map();
  private loadingProductSpecificationFilterOptionsPromises: Map<string, Promise<ProductSpecificationFilterOption[]>> = new Map();
  LoadProductSpecificationFilterOptions(productTypeId: string): Promise<ProductSpecificationFilterOption[]> {
    if (!productTypeId) {
      return Promise.resolve([]);
    }

    const cached = this.productSpecificationFilterOptionsCache.get(productTypeId);
    if (cached && (Date.now() - cached.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(cached.data);
    }

    const inFlight = this.loadingProductSpecificationFilterOptionsPromises.get(productTypeId);
    if (inFlight) {
      return inFlight;
    }

    const request = new GetListProductsFiltersRequest();
    request.filterProductTypeId = productTypeId;
    request.includeFilterProductSpecificationOptions = true;

    const promise = this.situViewClient.getListProductsFilters(request)
      .then((response: GetListProductsFiltersResponse) => {
        if (response.success) {
          const data = response.filterProductSpecificationOptions ?? [];
          this.productSpecificationFilterOptionsCache.set(productTypeId, {
            timestamp: Date.now(),
            data: data
          });
          return data;
        } else {
          return [];
        }
      })
      .finally(() => {
        this.loadingProductSpecificationFilterOptionsPromises.delete(productTypeId);
      });

    this.loadingProductSpecificationFilterOptionsPromises.set(productTypeId, promise);
    return promise;
  }

  private productWorkflowConfigurationCache: ICache<ProductWorkflow> | undefined;
  private loadingProductWorkflowConfigurationPromise: Promise<ProductWorkflow[]> | null = null;
  LoadProductWorkflowConfiguration(): Promise<ProductWorkflow[]> {
    if (this.productWorkflowConfigurationCache &&
      (Date.now() - this.productWorkflowConfigurationCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.productWorkflowConfigurationCache.data);
    } else if (this.loadingProductWorkflowConfigurationPromise) {
      return this.loadingProductWorkflowConfigurationPromise;
    } else {
      var request = new GetProductWorkflowConfigurationRequest();
      this.loadingProductWorkflowConfigurationPromise = this.situViewClient.getProductWorkflowStates(request)
          .then((response: GetProductWorkflowConfigurationResponse) => {
            if (response.success) {
              this.productWorkflowConfigurationCache = {
                timestamp: Date.now(),
                data: response.workflowConfiguration ?? []
              };
              return this.productWorkflowConfigurationCache.data;
            } else {
              return [];
            }
          }).finally(() => {
            this.loadingProductWorkflowConfigurationPromise = null;
          });
      return this.loadingProductWorkflowConfigurationPromise;
    }
  }
  
  private productThemesCache: ICache<Theme> | undefined;
  private loadingThemesPromise: Promise<Theme[]> | null = null;
  LoadProductThemes(companyId: string, productId: string): Promise<Theme[]> {
    if (this.productThemesCache &&
      this.productThemesCache.companyId === companyId &&
      this.productThemesCache.subjectId === productId &&
      (Date.now() - this.productThemesCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.productThemesCache.data);
    } else if (this.loadingThemesPromise) {
      return this.loadingThemesPromise;
    } else {
      var request = new GetProductThemesRequest();
      request.companyId = companyId;
      request.productId = productId;
      this.loadingThemesPromise = this.situViewClient.getProductThemes(request)
          .then((response: GetProductThemesResponse) => {
            if (response.success) {
              this.productThemesCache = {
                timestamp: Date.now(),
                companyId: companyId,
                subjectId: productId,
                data: response.themes ?? []
              };
              return this.productThemesCache.data;
            } else {
              return [];
            }
          }).finally(() => {
            this.loadingThemesPromise = null;
          });
      return this.loadingThemesPromise;
    }
  }
  
  private productWorkflowHistoryCache: ICache<ProductWorkflowHistory> | undefined;
  private loadingProductWorkflowHistoryPromise: Promise<ProductWorkflowHistory[]> | null = null;
  LoadProductWorkflowHistory(companyId: string, productId: string): Promise<ProductWorkflowHistory[]> {
    if (this.productWorkflowHistoryCache &&
      this.productWorkflowHistoryCache.companyId === companyId &&
      this.productWorkflowHistoryCache.subjectId === productId &&
      (Date.now() - this.productWorkflowHistoryCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.productWorkflowHistoryCache.data);
    } else if (this.loadingProductWorkflowHistoryPromise) {
      return this.loadingProductWorkflowHistoryPromise;
    } else {
      var request = new GetProductWorkflowHistoryRequest();
      request.companyId = companyId;
      request.productId = productId;
      this.loadingProductWorkflowHistoryPromise = this.situViewClient.getProductWorkflowHistory(request)
          .then((response: GetProductWorkflowHistoryResponse) => {
            if (response.success) {
              this.productWorkflowHistoryCache = {
                timestamp: Date.now(),
                companyId: companyId,
                subjectId: productId,
                data: response.workflowHistory ?? []
              };
              return this.productWorkflowHistoryCache.data;
            } else {
              return [];
            }
          }).finally(() => {
            this.loadingProductWorkflowHistoryPromise = null;
          });
      return this.loadingProductWorkflowHistoryPromise;
    }
  }

  TriggerProductWorkflow(companyId: string, productId: string, trigger?: ProductWorkflowTrigger, comments?: string): Promise<ProductWorkflow | undefined> {
    let triggerProductWorkflowRequest = new TriggerProductWorkflowRequest();
    triggerProductWorkflowRequest.companyId = companyId;
    triggerProductWorkflowRequest.productId = productId;
    triggerProductWorkflowRequest.workflowTrigger = trigger;
    triggerProductWorkflowRequest.comments = comments;

    return this.situViewClient.triggerProductWorkflow(triggerProductWorkflowRequest)
      .then((x: TriggerProductWorkflowResponse) => {
        if (x.success) {
          this.productWorkflowHistoryCache = undefined;
          return x.currentWorkflowState;
        } else {
          return undefined;
        }
      })
      .catch((err: any) => {
        return undefined;
      });
  }

  LoadThemeReplacementTags(includeProductTypes: boolean = false, includeThemeLineItemTypes: boolean = false): Promise<ThemeReplacementTag[]> {

      var request = new ListThemeReplacementTagsRequest();
      request.includeProductTypes = includeProductTypes;
      request.includeThemeLineItemTypes = includeThemeLineItemTypes;
      return this.situViewClient.listThemeReplacementTags(request)
          .then((response: ListThemeReplacementTagsResponse) => {
            if (response.success && response.themeReplacementTags) {
              return response.themeReplacementTags;
            } else {
              return [];
            }
          });
  }

  async UpdateThemeReplacementTag(themeReplacementTag: ThemeReplacementTag) : Promise<ThemeReplacementTag | undefined> {
    var request = new UpdateThemeReplacementTagRequest();
    request.themeReplacementTag = themeReplacementTag;

    var response = await this.situViewClient.updateThemeReplacementTag(request);

    return response?.themeReplacementTag;
  }

  async AddThemeReplacementTag(themeReplacementTag: ThemeReplacementTag): Promise<ThemeReplacementTag | undefined> {
    var request = new UpdateThemeReplacementTagRequest();
    request.themeReplacementTag = themeReplacementTag;

    var response = await this.situViewClient.addThemeReplacementTag(request);

    return response?.themeReplacementTag;
  }
  
  RefreshData() {
    this.oldCacheKey = this.cacheKey;
    this.cacheKey = Guid.create();
  }

  @Cache({
    duration: 900000
  })
  private listProductsInternal(request: ListProductsRequest, cacheKey: Guid, oldCacheKey: Guid): Promise<ListProductsResponse> {
    return this.situViewClient.listProducts(request);
  }

  private colorPool: string[] = [
    'rgb(255, 223, 186)', // Peach
    'rgb(255, 179, 186)', // Light Pink
    'rgb(255, 205, 210)', // Pink
    'rgb(197, 202, 233)', // Lavender
    'rgb(220, 237, 200)', // Tea Green
    'rgb(255, 240, 219)', // Unbleached Silk
    'rgb(255, 209, 220)', // Pink Lace
    'rgb(234, 224, 200)', // Almond
    'rgb(255, 228, 225)', // Misty Rose
    'rgb(255, 218, 185)', // Peach
    'rgb(238, 232, 170)', // Pale Goldenrod
    'rgb(255, 250, 205)', // Lemon Chiffon
    'rgb(250, 235, 215)', // Antique White
    'rgb(245, 245, 220)', // Beige
    'rgb(255, 239, 213)', // Papaya Whip
    'rgb(255, 235, 205)', // Blanched Almond
    'rgb(255, 228, 196)', // Bisque
    'rgb(255, 222, 173)', // Navajo White
    'rgb(255, 228, 181)', // Moccasin
    'rgb(255, 248, 220)', // Cornsilk
    'rgb(255, 250, 240)', // Floral White
    'rgb(253, 245, 230)', // Old Lace
    'rgb(250, 240, 230)', // Linen
    'rgb(255, 245, 238)', // Seashell
    'rgb(245, 255, 250)', // Mint Cream
    'rgb(240, 255, 240)', // Honeydew
    'rgb(240, 255, 255)', // Azure
    'rgb(240, 248, 255)', // Alice Blue
    'rgb(230, 230, 250)', // Lavender
    'rgb(255, 240, 245)', // Lavender Blush
    'rgb(255, 228, 225)', // Misty Rose
    'rgb(255, 255, 240)', // Ivory
    'rgb(250, 250, 210)', // Light Goldenrod Yellow
    'rgb(255, 255, 224)', // Light Yellow
    'rgb(224, 255, 255)', // Light Cyan
    'rgb(215, 255, 255)', // Pale Turquoise
    'rgb(215, 228, 255)', // Light Sky Blue
    'rgb(235, 245, 255)', // Light Steel Blue
    'rgb(220, 245, 245)', // Pale Blue
    'rgb(196, 228, 255)', // Light Blue
    'rgb(212, 255, 255)', // Powder Blue
    'rgb(187, 255, 255)', // Pale Cyan
    'rgb(176, 224, 230)', // Powder Blue
    'rgb(173, 216, 230)', // Light Blue
    'rgb(135, 206, 250)', // Light Sky Blue
    'rgb(135, 206, 235)', // Sky Blue
    'rgb(224, 255, 255)', // Light Cyan
    'rgb(240, 255, 255)', // Azure
    'rgb(225, 255, 255)'  // Pale Cyan
  ];
}

export class ProductTypeWithColor extends ProductType {
  color: string;

  constructor(data: IProductType, color: string) {
    super(data);
    this.color = color;
  }
}


export class PaginatedProducts {
  products?: Product2[];
  pageNumber?: number;
  pageSize?: number;
  totalItems?: number;
  validFilters?: GetListProductsFiltersResponse;

  constructor(products?: Product2[], pageNumber?: number, pageSize?: number, totalItems?: number) {
    this.products = products;
    this.pageNumber = pageNumber;
    this.pageSize = pageSize;
    this.totalItems = totalItems;
  }
}


export class PaginatedRequestedProducts {
  requestedProducts?: RequestedProduct[];
  pageNumber?: number;
  pageSize?: number;
  totalItems?: number;
  validFilters?: GetListRequestedProductsFiltersResponse;

  constructor(requestedProducts?: RequestedProduct[], pageNumber?: number, pageSize?: number, totalItems?: number) {
    this.requestedProducts = requestedProducts;
    this.pageNumber = pageNumber;
    this.pageSize = pageSize;
    this.totalItems = totalItems;
  }
}